/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2024 Wecare Dynamics Inc.



 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

import { lazy } from "react";
import Icon from "@mui/material/Icon";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useAuth } from "context/userContext";

// Lazy route elements keep navigation metadata eager while deferring page bundles.
const Dashboard = lazy(() => import("layouts/dashboard"));
const Device = lazy(() => import("layouts/device"));
const SignIn = lazy(() => import("layouts/authentications/sign-in"));
const Change = lazy(() => import("layouts/authentications/change-password"));
const Forgot = lazy(() => import("layouts/authentications/forgot-password"));
const BusinessSettingPage = lazy(() => import("layouts/settings/business-page"));
const Resident = lazy(() => import("layouts/settings/resident-page"));
const General = lazy(() => import("layouts/settings/general-page"));
const DisplaySingleBusiness = lazy(() => import("layouts/settings/business-page/displaySingleBusiness"));
const Account = lazy(() => import("layouts/account"));
const Info = lazy(() => import("layouts/info"));
const MeasurementPage = lazy(() => import("layouts/info/measurement-page"));
const ResidentInfo = lazy(() => import("layouts/overview/resident-page"));
const HealthEducation = lazy(() => import("layouts/health-education"));
const HealthPrescription = lazy(() => import("layouts/health-prescription"));
const UnitOverView = lazy(() => import("layouts/overview/unit-page"));
const AlertHistoryPage = lazy(() => import("layouts/dashboard/alert-history"));
const CenterPage = lazy(() => import("layouts/dashboard/center-page"));
const BusinessInfoPage = lazy(() => import("layouts/overview/business-page"));
const CenterInfoPage = lazy(() => import("layouts/overview/center-page"));
const ReportPage = lazy(() => import("layouts/report"));
const RoomSettingPage = lazy(() => import("layouts/settings/room-page"));
const ResidentRoomPage = lazy(() => import("layouts/overview/resident-room-page"));
export interface iRoute {
    type?: string;
    name?: string;
    key: string;
    remote?: boolean; // Remote controlled route,
    route?: string;
    icon?: JSX.Element;
    component?: JSX.Element;
    noCollapse?: boolean;
    collapse?: iRoute[];
    // Below properties are inserted in later stage
    title?: string;
    href?: string;
    isPublic?: boolean;
    requiredMenuKey?: string;
    requiredRoles?: string[];
}

const publicHiddenRoutes: iRoute[] = [
    {
        type: "hide",
        name: "Authentication",
        key: "authentication",
        collapse: [
            {
                name: "Sign In",
                key: "sign-in",
                route: "/sign-in",
                component: <SignIn />,
                noCollapse: true,
                isPublic: true,
            },
            {
                name: "Forgot Password",
                key: "forgot-password",
                route: "/forgot-password",
                component: <Forgot />,
                noCollapse: true,
                isPublic: true,
            },
            {
                name: "Change Password",
                key: "change-password",
                route: "/change-password",
                component: <Change />,
                noCollapse: true,
                isPublic: true,
            },
            {
                name: "Activate Account",
                key: "activate",
                route: "/activate",
                component: <Change />,
                noCollapse: true,
                isPublic: true,
            },
        ],
    },
    {
        type: "hide",
        name: "HealthEducation",
        key: "education",
        collapse: [
            {
                name: "Health-Education",
                key: "education",
                route: "/education/",
                component: <HealthEducation />,
                isPublic: true,
            },
        ],
    },
];

const routes: iRoute[] = [
    { type: "title", title: "General", key: "general-pages" },
    {
        type: "collapse",
        name: "Dashboard",
        key: "dashboard",
        remote: true,
        icon: <Icon fontSize="medium">dashboard</Icon>,
        requiredMenuKey: "dashboard",
        collapse: [
            {
                name: "Dashboard",
                key: "dashboard",
                route: "/dashboard",
                component: <Dashboard />,
                requiredMenuKey: "dashboard",
            },
        ],
    },
    {
        type: "collapse",
        name: "dashboard-v2",
        key: "dashboard-v2",
        remote: true,
        icon: <Icon fontSize="medium">dashboard</Icon>,
        route: "/dashboard/dashboard-center",
        component: <CenterPage />,
        noCollapse: true,
        requiredMenuKey: "dashboard-v2",
        requiredRoles: ["rel-admin", "business-admin", "center-admin", "center-staff"],
    },
    {
        type: "collapse",
        name: "Report",
        key: "report",
        route: "/report",
        remote: true,
        component: <ReportPage />,
        icon: <Icon fontSize="medium">summarize</Icon>,
        noCollapse: true,
        requiredMenuKey: "report",
    },
    {
        type: "collapse",
        name: "Device",
        key: "device",
        route: "/device",
        component: <Device />,
        icon: <Icon fontSize="medium">wifi</Icon>,
        noCollapse: true,
        requiredMenuKey: "device",
    },
    {
        type: "collapse",
        name: "overview",
        key: "overview",
        remote: false,
        icon: <Icon fontSize="medium">info</Icon>,
        collapse: [
            {
                name: "overview-business",
                key: "overview-business",
                route: "/overview/overview-business",
                remote: true,
                component: <BusinessInfoPage />,
                requiredMenuKey: "overview-business",
            },
            {
                name: "Center",
                key: "overview-center",
                route: "/overview/overview-center",
                remote: true,
                component: <CenterInfoPage />,
                requiredMenuKey: "overview-center",
            },
        ],
    },
    {
        type: "collapse",
        name: "Settings",
        key: "settings",
        remote: true,
        icon: <Icon fontSize="medium">settings</Icon>,
        collapse: [
            {
                name: "Business",
                key: "business",
                route: "/settings/business",
                remote: true,
                component: <BusinessSettingPage />,
                requiredMenuKey: "business",
            },
            {
                name: "Resident",
                key: "resident",
                route: "/settings/resident",
                remote: true,
                component: <Resident />,
                requiredMenuKey: "resident",
            },
            {
                name: "General",
                key: "general",
                route: "/settings/general",
                remote: true,
                component: <General />,
                requiredMenuKey: "general",
            },
        ],
    },
    {
        type: "collapse",
        name: "Account",
        key: "account",
        route: "/account",
        remote: true,
        component: <Account />,
        icon: <Icon fontSize="medium">person_add</Icon>,
        noCollapse: true,
        requiredMenuKey: "account",
    },
    { type: "title", title: "analysis", key: "info-pages" },
    {
        type: "collapse",
        name: "MeasurementAndSurvey",
        key: "daily-measurement-and-survey",
        route: "/daily-measurement-and-survey",
        component: <Info />,
        icon: <Icon fontSize="medium">assessment</Icon>,
        noCollapse: true,
        requiredMenuKey: "daily-measurement-and-survey",
    },
    {
        type: "collapse",
        name: "sleeproutine",
        key: "sleeproutine",
        route: "/sleeproutine",
        component: <UnitOverView type="report" />,
        icon: <Icon fontSize="medium">dashboard</Icon>,
        noCollapse: true,
        requiredMenuKey: "sleeproutine",
    },
    {
        type: "collapse",
        name: "roomactivity",
        key: "roomactivity",
        route: "/roomactivity",
        component: <UnitOverView type="live" />,
        icon: <CreditCardIcon sx={{ fontSize: "16px !important" }}></CreditCardIcon>,
        noCollapse: true,
        requiredMenuKey: "roomactivity",
    },
    {
        type: "hide",
        name: "roomactivity",
        key: "roomactivity",
        requiredMenuKey: "roomactivity",
        collapse: [
            {
                name: "RoomActivityData",
                key: "room-activity-data",
                route: "/roomactivity/room-activity-data",
                component: <ResidentRoomPage />,
            },
            {
                name: "roomactivity-profile",
                key: "roomactivity-profile",
                route: "/roomactivity/profile",
                component: <ResidentInfo />,
            },
        ],
    },
    ...publicHiddenRoutes,
    {
        type: "hide",
        name: "settings",
        key: "settings",
        collapse: [
            {
                name: "Display Single Business",
                key: "display single business",
                route: "/setting/business/:name",
                component: <DisplaySingleBusiness />,
                requiredMenuKey: "business",
            },
        ],
    },
    {
        type: "hide",
        name: "Dashboard",
        key: "dashboard",
        collapse: [
            {
                name: "Alert History",
                key: "alert-history",
                route: "/dashboard/alert-history",
                component: <AlertHistoryPage />,
                requiredMenuKey: "dashboard",
            },
        ],
    },
    {
        type: "hide",
        name: "Dashboard",
        key: "dashboard",
        collapse: [
            {
                name: "dashboard-profile",
                key: "dashboard-profile",
                route: "/dashboard/profile",
                component: <ResidentInfo />,
                requiredMenuKey: "dashboard",
            },
        ],
    },
    {
        type: "hide",
        name: "MeasurementAndSurvey",
        key: "daily-measurement-and-survey",
        collapse: [
            {
                name: "MeasurementAndSurvey",
                key: "daily-measurement-and-survey",
                route: "/daily-measurement-and-survey/physiological-data",
                component: <MeasurementPage />,
                requiredMenuKey: "daily-measurement-and-survey",
            },
        ],
    },
    {
        type: "hide",
        name: "HealthPrescription",
        key: "prescription",
        requiredMenuKey: "prescription",
        collapse: [
            {
                name: "Health-Prescription",
                key: "prescription",
                route: "/prescription/",
                component: <HealthPrescription />,
                requiredMenuKey: "prescription",
            },
        ],
    },
    // {
    //     type: "hide",
    //     name: "overview",
    //     key: "overview",
    //     remote: true,
    //     icon: <Icon fontSize="medium">info</Icon>,
    //     collapse: [
    //         {
    //             name: "overview-profile",
    //             key: "overview-profile",
    //             route: "/overview/profile",
    //             component: <ResidentInfo />,
    //         },
    //     ],
    // },
    {
        type: "hide",
        name: "report2",
        key: "report2",
        remote: true,
        icon: <Icon fontSize="medium">info</Icon>,
        requiredMenuKey: "report2",
        collapse: [
            {
                name: "report2",
                key: "report2",
                route: "/report2",
                component: <RoomSettingPage />,
                requiredMenuKey: "report2",
            },
        ],
    },
    // {
    //     type: "hide",
    //     name: "test",
    //     key: "test",
    //     route: "/test",
    //     component: <Demo />,
    //     icon: <Icon fontSize="medium">test</Icon>,
    //     noCollapse: true,
    // },
];

export const useAuthRoutes = () => {
    const { currentUser } = useAuth();
    if (currentUser?.role === "user") {
        const UserRoutes: iRoute[] = [
            { type: "title", title: "General", key: "general-pages" },
            {
                type: "hide",
                name: "overview",
                key: "overview",
                remote: true,
                icon: <Icon fontSize="medium">info</Icon>,
                collapse: [
                    {
                        name: "overview-profile",
                        key: "overview-profile",
                        route: "/overview/profile",
                        component: <ResidentInfo />,
                        requiredRoles: ["user"],
                    },
                ],
            },
            ...publicHiddenRoutes,
        ];
        return UserRoutes;
    } else if (currentUser?.role === "installer") {
        const UserRoutes: iRoute[] = [
            { type: "title", title: "General", key: "general-pages" },
            {
                type: "collapse",
                name: "device-installation",
                key: "business",
                route: "/device-installation",
                component: <BusinessSettingPage />,
                icon: <Icon fontSize="medium">settings</Icon>,
                noCollapse: true,
                requiredRoles: ["installer"],
            },
            {
                type: "collapse",
                name: "device-list",
                key: "device-list",
                route: "/device-list",

                component: <Device />,
                icon: <Icon fontSize="medium">wifi</Icon>,
                noCollapse: true,
                requiredRoles: ["installer"],
            },
            ...publicHiddenRoutes,
            {
                type: "hide",
                name: "settings",
                key: "settings",
                collapse: [
                    {
                        name: "Display Single Business",
                        key: "display single business",
                        route: "/setting/business/:name",
                        component: <DisplaySingleBusiness />,
                        requiredRoles: ["installer"],
                    },
                ],
            },
        ];
        return UserRoutes;
    } else {
        return routes;
    }
};

export default routes;
